/**
 * Folders API endpoints
 */
import type {
    ApiResponse,
    Folder,
    FolderTreeNode,
    FolderInput,
    FolderUpdate,
    FolderItemInput,
    Report,
    Dashboard,
    Chart,
    Form,
    TabGroup,
} from "../lib/types";
import { authenticatedRequest } from "../lib/api";

export async function getFolders(token: string): Promise<FolderTreeNode[]> {
    return authenticatedRequest<ApiResponse<FolderTreeNode[]>>(
        "/api/folders",
        token
    ).then((res) => res.data);
}

export async function getFolder(
    token: string,
    id: string
): Promise<
    Folder & {
        reports: Report[];
        dashboards: Dashboard[];
        charts: Chart[];
        forms: Form[];
        tabGroups: TabGroup[];
    }
> {
    return authenticatedRequest<
        ApiResponse<
            Folder & {
                reports: Report[];
                dashboards: Dashboard[];
                charts: Chart[];
                forms: Form[];
                tabGroups: TabGroup[];
            }
        >
    >(`/api/folders/${id}`, token).then((res) => res.data);
}

export async function createFolder(
    token: string,
    input: FolderInput
): Promise<Folder> {
    return authenticatedRequest<ApiResponse<Folder>>("/api/folders", token, {
        method: "POST",
        body: JSON.stringify(input),
    }).then((res) => res.data);
}

export async function updateFolder(
    token: string,
    id: string,
    input: FolderUpdate
): Promise<Folder> {
    return authenticatedRequest<ApiResponse<Folder>>(`/api/folders/${id}`, token, {
        method: "PUT",
        body: JSON.stringify(input),
    }).then((res) => res.data);
}

export async function deleteFolder(token: string, id: string): Promise<void> {
    return authenticatedRequest<void>(`/api/folders/${id}`, token, {
        method: "DELETE",
    });
}

export async function addFolderItem(
    token: string,
    folderId: string,
    input: FolderItemInput
): Promise<void> {
    return authenticatedRequest<void>(`/api/folders/${folderId}/items`, token, {
        method: "POST",
        body: JSON.stringify(input),
    });
}

export async function removeFolderItem(
    token: string,
    folderId: string,
    itemType: "report" | "dashboard" | "chart" | "form" | "tab_group",
    itemId: string
): Promise<void> {
    return authenticatedRequest<void>(
        `/api/folders/${folderId}/items/${itemType}/${itemId}`,
        token,
        {
            method: "DELETE",
        }
    );
}

export async function getRootItems(
    token: string
): Promise<{
    reports: Report[];
    dashboards: Dashboard[];
    charts: Chart[];
    forms: Form[];
    tabGroups: TabGroup[];
}> {
    return authenticatedRequest<
        ApiResponse<{
            reports: Report[];
            dashboards: Dashboard[];
            charts: Chart[];
            forms: Form[];
            tabGroups: TabGroup[];
        }>
    >("/api/folders/root/items", token).then((res) => res.data);
}

export async function getFolderForItem(
    token: string,
    itemType: "report" | "dashboard" | "chart" | "form" | "tab_group",
    itemId: string
): Promise<{ folderId: string | null }> {
    return authenticatedRequest<ApiResponse<{ folderId: string | null }>>(
        `/api/folders/item/${itemType}/${itemId}`,
        token
    ).then((res) => res.data);
}
